import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { MapPin, Phone, Mail, Instagram, ChevronLeft, ChevronRight, BookOpen, ShoppingBag, ExternalLink, Sparkles, Shirt } from 'lucide-react';
import { getApiUrl, resolveImageUrl } from '../utils/api';
import '../styles/book-flip.css';
import '../styles/storefront.css';

const Home = () => {
  const navigate = useNavigate();
  const [currentPortfolioIndex, setCurrentPortfolioIndex] = useState(0);
  const [isFlipping, setIsFlipping] = useState(false);
  const [flipDirection, setFlipDirection] = useState('next');
  const [nextIndex, setNextIndex] = useState(0);
  
  // State for data from backend
  const [businessInfo, setBusinessInfo] = useState(null);
  const [services, setServices] = useState([]);
  const [portfolioItems, setPortfolioItems] = useState([]);
  const [storefrontProducts, setStorefrontProducts] = useState([]);
  const [loading, setLoading] = useState(true);

  // Fetch data from backend on mount
  useEffect(() => {
    const fetchData = async () => {
      try {
        const apiUrl = getApiUrl();
        const [businessRes, servicesRes, portfolioRes, storefrontRes] = await Promise.all([
          fetch(`${apiUrl}/api/business-info`),
          fetch(`${apiUrl}/api/services`),
          fetch(`${apiUrl}/api/portfolio`),
          fetch(`${apiUrl}/api/storefront-products`)
        ]);
        
        const [businessData, servicesData, portfolioData, storefrontData] = await Promise.all([
          businessRes.json(),
          servicesRes.json(),
          portfolioRes.json(),
          storefrontRes.json()
        ]);
        
        setBusinessInfo(businessData);
        setServices(servicesData);
        setPortfolioItems(portfolioData);
        setStorefrontProducts(storefrontData);
      } catch (error) {
        console.error('Error fetching data:', error);
      } finally {
        setLoading(false);
      }
    };
    
    fetchData();
  }, []);

  const handleBooking = (location) => {
    if (location === 'Baldwin') {
      window.open(businessInfo?.baldwin_booking_url || 'https://theslaystationny.glossgenius.com/', '_blank');
    } else {
      window.open(businessInfo?.islandia_booking_url || 'https://theslaystationny.glossgenius.com/services', '_blank');
    }
  };

  const handleNextPortfolio = () => {
    if (!isFlipping && portfolioItems.length > 0) {
      const newIndex = (currentPortfolioIndex + 1) % portfolioItems.length;
      setNextIndex(newIndex);
      setFlipDirection('next');
      setIsFlipping(true);
      
      setTimeout(() => {
        setCurrentPortfolioIndex(newIndex);
        setIsFlipping(false);
      }, 800);
    }
  };

  const handlePrevPortfolio = () => {
    if (!isFlipping && portfolioItems.length > 0) {
      const newIndex = (currentPortfolioIndex - 1 + portfolioItems.length) % portfolioItems.length;
      setNextIndex(newIndex);
      setFlipDirection('prev');
      setIsFlipping(true);
      
      setTimeout(() => {
        setCurrentPortfolioIndex(newIndex);
        setIsFlipping(false);
      }, 800);
    }
  };

  const currentItem = portfolioItems[currentPortfolioIndex];
  const nextItem = portfolioItems[nextIndex];

  if (loading) {
    return (
      <div className="home-page-centered" style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div className="marble-background"></div>
        <p style={{ color: '#b8860b', fontStyle: 'italic', fontSize: '1.2rem' }}>Loading...</p>
      </div>
    );
  }

  return (
    <div className="home-page-centered">
      {/* Marble Background Overlay */}
      <div className="marble-background"></div>

      {/* Hero Profile Section */}
      <section className="hero-profile-section">
        <div className="container-centered">
          <div className="profile-layout">
            {/* Profile Image with Title Above */}
            <div className="profile-card">
              <h1 className="profile-greeting-gold">{businessInfo?.bio_title_1 || 'Meet Your'}</h1>
              <h2 className="profile-title-gold">{businessInfo?.bio_title_2 || 'Esthetician'}</h2>
              <div className="arched-frame-gold">
                <img 
                  src={resolveImageUrl(businessInfo?.profile_image) || "https://customer-assets.emergentagent.com/job_elegant-esthetics/artifacts/jqqwg3e7_IMG_9387.jpeg"}
                  alt="The Slay Station"
                  className="profile-image-arched"
                />
              </div>
            </div>

            {/* About Section */}
            <div className="profile-about">
              <p className="profile-description">
                {businessInfo?.bio_text_1 || 'Beauty should feel effortless and personal. At The Slay Station, every service is created with intention, care, and attention to detail. Each appointment is unhurried, personalized, and designed to help you feel comfortable, supported, and confident from start to finish.'}
              </p>
              <p className="profile-description">
                {businessInfo?.bio_text_2 || 'Led by licensed esthetician and makeup artist Apolonia, this is a space where beauty feels gentle, elevated, and deeply personal.'}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Booking Locations - Right After Bio */}
      <section className="location-selection-section">
        <div className="container-centered">
          <div className="location-select-header-gold">
            <MapPin size={20} className="icon-gold" />
            <span>Book Your Appointment</span>
          </div>

          <div className="location-cards-list">
            <div className="location-card-item-gold">
              <div className="location-card-content">
                <h4 className="location-card-name">BALDWIN</h4>
                <p className="location-card-hours">Wednesday - Saturday: 11:00 AM - 10:00 PM</p>
              </div>
              <button 
                className="select-button-gold"
                onClick={() => handleBooking('Baldwin')}
              >
                BOOK NOW
              </button>
            </div>

            <div className="location-card-item-gold">
              <div className="location-card-content">
                <h4 className="location-card-name">HOME STUDIO ISLANDIA</h4>
                <p className="location-card-hours">Monday & Tuesday: 5:00 PM - 10:00 PM</p>
              </div>
              <button 
                className="select-button-gold"
                onClick={() => handleBooking('Islandia')}
              >
                BOOK NOW
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Portfolio Preview Section */}
      {portfolioItems.length > 0 && (
        <section className="portfolio-preview-section">
          <div className="container-centered">
            <div className="portfolio-header">
              <BookOpen size={32} className="icon-gold" />
              <h3 className="portfolio-preview-title">Our Transformation Book</h3>
              <p className="portfolio-preview-subtitle">Witness the artistry of our work</p>
            </div>

            <div className="flip-book-container home-book">
              {/* The Open Book */}
              <div className="flip-book">
                {/* Left Page (static) */}
                <div className="book-page book-page-left">
                  <div 
                    className="page-image-half page-image-left"
                    style={{ 
                      backgroundImage: `url(${resolveImageUrl(currentItem?.beforeImage)})`,
                      backgroundPosition: 'left center'
                    }}
                  />
                </div>

                {/* Right Page (static - shows under flipping page) */}
                <div className="book-page book-page-right">
                  <div 
                    className="page-image-half page-image-right"
                    style={{ 
                      backgroundImage: `url(${resolveImageUrl(isFlipping ? nextItem?.beforeImage : currentItem?.beforeImage)})`,
                      backgroundPosition: 'right center'
                    }}
                  />
                </div>

                {/* Flipping Page Overlay */}
                {isFlipping && (
                  <div className={`flipping-page ${flipDirection === 'next' ? 'flip-to-left' : 'flip-to-right'}`}>
                    {/* Front of flipping page (current image right half) */}
                    <div className="flip-page-front">
                      <div 
                        className="page-image-half"
                        style={{ 
                          backgroundImage: `url(${resolveImageUrl(flipDirection === 'next' ? currentItem?.beforeImage : nextItem?.beforeImage)})`,
                          backgroundPosition: 'right center'
                        }}
                      />
                    </div>
                    {/* Back of flipping page (next image left half) */}
                    <div className="flip-page-back">
                      <div 
                        className="page-image-half"
                        style={{ 
                          backgroundImage: `url(${resolveImageUrl(flipDirection === 'next' ? nextItem?.beforeImage : currentItem?.beforeImage)})`,
                          backgroundPosition: 'left center'
                        }}
                      />
                    </div>
                  </div>
                )}

                {/* Book Spine (center) - only visible during flip */}
                <div className={`book-spine ${isFlipping ? 'spine-visible' : ''}`}></div>

                {/* Gold Frame Border */}
                <div className="book-gold-frame"></div>
              </div>

              {/* Navigation Arrows */}
              <button 
                className="flip-nav flip-nav-left"
                onClick={handlePrevPortfolio}
                disabled={isFlipping}
                aria-label="Previous page"
              >
                <ChevronLeft size={28} />
              </button>
              <button 
                className="flip-nav flip-nav-right"
                onClick={handleNextPortfolio}
                disabled={isFlipping}
                aria-label="Next page"
              >
                <ChevronRight size={28} />
              </button>

              {/* Treatment Info Below Book */}
              <div className="flip-book-info">
                <div className="chapter-badge-gold">
                  {services.find(s => s.id === currentItem?.category)?.name || currentItem?.category}
                </div>
                <h3 className="treatment-title-gold">{currentItem?.treatmentName}</h3>
                <p className="treatment-desc">{currentItem?.description}</p>
              </div>

              <button 
                className="view-full-portfolio-btn"
                onClick={() => navigate('/portfolio')}
              >
                Open Full Portfolio Book
              </button>
            </div>
          </div>
        </section>
      )}

      {/* Services Section */}
      <section className="services-list-section-gold">
        <div className="container-centered">
          <h3 className="services-heading-gold">Our Services</h3>
          <p className="services-subtitle">Each service tells its own beautiful story</p>
          <div className="services-simple-list">
            {services.map((service) => (
              <div 
                key={service.id}
                className="service-list-item-gold"
                onClick={() => navigate('/portfolio', { state: { category: service.id } })}
              >
                <span className="service-name">{service.name}</span>
                <span className="service-arrow">→</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Curated Favorites Preview Section */}
      <section className="home-storefront-preview" data-testid="home-storefront-preview">
        <div className="container-centered">
          <div className="storefront-section-header">
            <div className="storefront-eyebrow">
              <Sparkles size={16} className="icon-gold" />
              <span>The Edit</span>
              <Sparkles size={16} className="icon-gold" />
            </div>
            <h2 className="storefront-section-title">A Few of My Favorites</h2>
            <p className="storefront-section-subtitle">
              Suggested products & beauty essentials I personally love
            </p>
          </div>

          {storefrontProducts.length > 0 && (
            <div className="home-storefront-preview-grid">
              {storefrontProducts.slice(0, 4).map((product) => (
                <article
                  key={product.id}
                  className="product-card"
                  data-testid={`home-product-card-${product.id}`}
                >
                  <a
                    href={product.amazon_link}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="product-card-image-link"
                  >
                    <div className="product-card-arch">
                      <div className="product-card-image-wrap">
                        {product.image ? (
                          <img
                            src={resolveImageUrl(product.image)}
                            alt={product.name}
                            className="product-card-image"
                            loading="lazy"
                          />
                        ) : (
                          <div className="product-card-placeholder">
                            <ShoppingBag size={32} className="icon-gold" />
                          </div>
                        )}
                      </div>
                    </div>
                  </a>
                  <div className="product-card-body">
                    <h3 className="product-card-name">{product.name}</h3>
                    {product.note && (
                      <p className="product-card-note">{product.note}</p>
                    )}
                  </div>
                </article>
              ))}
            </div>
          )}

          <div className="home-storefront-preview-cta-wrap">
            <div className="storefront-cta-row">
              <button
                type="button"
                onClick={() => navigate('/favorites')}
                className="storefront-hero-cta"
                data-testid="home-explore-favorites-btn"
              >
                <ShoppingBag size={18} />
                <span>Shop My Amazon Picks</span>
                <ExternalLink size={16} />
              </button>
              {businessInfo?.mac_storefront_url && (
                <a
                  href={businessInfo.mac_storefront_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="storefront-hero-cta storefront-hero-cta-mac"
                  data-testid="home-shop-mac-btn"
                >
                  <Sparkles size={18} />
                  <span>Shop MAC With Me</span>
                  <ExternalLink size={16} />
                </a>
              )}
              {businessInfo?.ae_storefront_url && (
                <a
                  href={businessInfo.ae_storefront_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="storefront-hero-cta storefront-hero-cta-ae"
                  data-testid="home-shop-ae-btn"
                >
                  <Shirt size={18} />
                  <span>Shop American Eagle With Me</span>
                  <ExternalLink size={16} />
                </a>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Contact Section - At Bottom */}
      <section className="contact-bottom-section">
        <div className="container-centered">
          <h3 className="contact-bottom-title">Get In Touch</h3>
          <div className="contact-box-gold">
            <div className="contact-info-grid">
              <div className="contact-item">
                <Phone size={18} className="icon-gold" />
                <a href={`tel:${businessInfo?.phone}`}>{businessInfo?.phone || '(631) 772-9261'}</a>
              </div>
              <div className="contact-item">
                <Mail size={18} className="icon-gold" />
                <a href={`mailto:${businessInfo?.email}`}>{businessInfo?.email || 'Apolonia@glamouroverall.com'}</a>
              </div>
            </div>

            <h4 className="locations-heading">New York Locations</h4>
            <div className="locations-grid">
              <div className="location-item">
                <MapPin size={18} className="icon-gold" />
                <div>
                  <p className="location-name">Home Studio Islandia</p>
                  <p className="location-address">Islandia, NY</p>
                </div>
              </div>
              <div className="location-item">
                <MapPin size={18} className="icon-gold" />
                <div>
                  <p className="location-name">Baldwin Studio</p>
                  <p className="location-address">Baldwin, NY</p>
                </div>
              </div>
            </div>

            <h4 className="social-heading">Social Media</h4>
            <div className="social-links">
              <a href={businessInfo?.instagram || "https://www.instagram.com/theslaystationny"} target="_blank" rel="noopener noreferrer" className="social-link">
                <Instagram size={18} className="icon-gold" />
                <span>@theslaystationny</span>
              </a>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;
